import { useCallback, useEffect, useState } from 'react'
import { Activity, AlertTriangle, CreditCard, RefreshCw } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api'
import { ExportCsvButton, type CsvColumn } from '@/components/admin/ExportCsvButton'

// Admin Panel Redesign — ported from the old AdminPanel.tsx "credits" tab
// (date-range picker, summary cards, by-tier breakdown, by-user table, daily
// usage bar chart). Now fetches independently on mount + on date-range change
// instead of only on a manual "Load" click tied to shared dashboard state.
//
// Bug fix: the old `by_tier` type hardcoded { starter, pro, enterprise } —
// missing `freemium` (the platform's default tier) entirely, and using the
// key `pro` when the backend actually returns `professional`, so that card
// always rendered undefined. The backend already returns by_tier as a plain
// Record<string, number> with keys freemium/starter/professional/enterprise
// (and possibly others) — this fixes the frontend to match.
interface CreditUsageByUser {
  user_id: string
  email: string
  tier: string
  requests_used: number
  request_limit: number
  percentage_used: number
}

interface CreditUsage {
  total_requests: number
  by_tier: Record<string, number>
  by_user: CreditUsageByUser[]
  daily_usage: Array<{ date: string; requests: number }>
}

const TIER_DISPLAY_ORDER = ['freemium', 'starter', 'professional', 'enterprise']

function tierLabel(tier: string): string {
  if (!tier) return tier
  return tier.charAt(0).toUpperCase() + tier.slice(1)
}

function orderedTierEntries(byTier: Record<string, number>): Array<[string, number]> {
  return Object.entries(byTier).sort(([a], [b]) => {
    const rankA = TIER_DISPLAY_ORDER.indexOf(a)
    const rankB = TIER_DISPLAY_ORDER.indexOf(b)
    const normA = rankA === -1 ? TIER_DISPLAY_ORDER.length : rankA
    const normB = rankB === -1 ? TIER_DISPLAY_ORDER.length : rankB
    if (normA !== normB) return normA - normB
    return a.localeCompare(b)
  })
}

const csvColumns: CsvColumn<CreditUsageByUser>[] = [
  { header: 'Email', value: (u) => u.email },
  { header: 'Tier', value: (u) => tierLabel(u.tier) },
  { header: 'Requests Used', value: (u) => u.requests_used },
  { header: 'Request Limit', value: (u) => (u.request_limit === -1 ? 'Unlimited' : u.request_limit) },
  { header: '% Used', value: (u) => `${u.percentage_used.toFixed(1)}%` },
]

export default function CreditsSection() {
  const { toast } = useToast()
  const [creditUsage, setCreditUsage] = useState<CreditUsage | null>(null)
  const [creditUsageLoading, setCreditUsageLoading] = useState(false)
  const [creditUsageError, setCreditUsageError] = useState<string | null>(null)
  const [creditsStartDate, setCreditsStartDate] = useState(
    () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  )
  const [creditsEndDate, setCreditsEndDate] = useState(() => new Date().toISOString().split('T')[0])

  const fetchCreditUsage = useCallback(async () => {
    setCreditUsageLoading(true)
    setCreditUsageError(null)
    try {
      const data = await apiClient.getCreditUsage(creditsStartDate, creditsEndDate)
      setCreditUsage(data as CreditUsage)
    } catch (e: any) {
      console.error('Failed to load credit usage data', e)
      setCreditUsageError(e.message || 'Failed to load credit usage data')
    } finally {
      setCreditUsageLoading(false)
    }
  }, [creditsStartDate, creditsEndDate])

  // Fetch on mount, and again whenever the date range changes.
  useEffect(() => {
    fetchCreditUsage()
  }, [fetchCreditUsage])

  const handleResetCredits = async (userId: string) => {
    if (!window.confirm("Reset this user's credit usage counter?")) return
    try {
      await apiClient.adminResetCredits({ user_id: userId })
      toast({ title: 'Credit usage reset' })
      await fetchCreditUsage()
    } catch (e: any) {
      toast({ title: 'Failed to reset credits', description: e.message, variant: 'destructive' })
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle>Credit Usage</CardTitle>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={creditsStartDate}
            onChange={(e) => setCreditsStartDate(e.target.value)}
            className="h-8 w-36"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="date"
            value={creditsEndDate}
            onChange={(e) => setCreditsEndDate(e.target.value)}
            className="h-8 w-36"
          />
          <Button size="sm" onClick={() => fetchCreditUsage()} disabled={creditUsageLoading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${creditUsageLoading ? 'animate-spin' : ''}`} />
            Load
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {creditUsageLoading && !creditUsage ? (
          <div className="text-center py-8">
            <Activity className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading credit usage data...</p>
          </div>
        ) : creditUsageError ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <p className="text-red-500 mb-4">{creditUsageError}</p>
            <Button onClick={() => fetchCreditUsage()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        ) : creditUsage ? (
          <>
            <div className="mb-6">
              <p className="text-2xl font-bold mb-4">Total Requests: {creditUsage.total_requests}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {orderedTierEntries(creditUsage.by_tier).map(([tier, count]) => (
                  <div key={tier} className="p-4 border border-border rounded bg-card">
                    <p className="text-sm text-muted-foreground">{tierLabel(tier)}</p>
                    <p className="text-xl font-bold">{count}</p>
                  </div>
                ))}
              </div>
            </div>

            {creditUsage.daily_usage && creditUsage.daily_usage.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Daily Request Volume</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={creditUsage.daily_usage}>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="requests" fill="#22c55e" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-semibold">
                  Top Users{' '}
                  <span className="text-xs font-normal text-muted-foreground">
                    (API calls in period / credit limit)
                  </span>
                </h3>
                <ExportCsvButton
                  rows={creditUsage.by_user || []}
                  columns={csvColumns}
                  filenamePrefix="credit_usage_by_user"
                  label="Export CSV"
                />
              </div>
              <div className="space-y-2">
                {creditUsage.by_user && creditUsage.by_user.length > 0 ? (
                  creditUsage.by_user.slice(0, 20).map((u, idx) => (
                    <div
                      key={u.user_id || idx}
                      className="flex justify-between items-center p-2 border border-border rounded"
                    >
                      <span>{u.email}</span>
                      <div className="flex gap-2 items-center">
                        <Badge>{tierLabel(u.tier)}</Badge>
                        <span title="API calls in period / credit limit">
                          {u.requests_used} / {u.request_limit === -1 ? '∞' : u.request_limit}
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => handleResetCredits(u.user_id)}>
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">No user data available</p>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No credit usage data available.</p>
            <Button className="mt-4" onClick={() => fetchCreditUsage()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Load Credit Usage
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
